"use client";

import { useState, useEffect, useRef } from "react";
import { ChevronRight, Plus, Video, Loader as Loader2 } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { motion } from "framer-motion";
import { toast } from "sonner";
const wpMedia = (window as unknown as {
  wp?: {
    media?: (options?: Record<string, any>) => {
      on: (event: string, callback: () => void) => void;
      state: () => {
        get: (selection: string) => {
          first: () => {
            toJSON: () => {
              url: string;
            };
          };
        };
      };
      open: () => void;
    };
  };
}).wp?.media;
const uploadVideoToServer = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append("title", file.name);
  formData.append("file", file, file.name);
  const headers: Record<string, string> = {};
  const wpNonce = (window as unknown as {
    wpApiSettings?: {
      nonce?: string;
    };
  }).wpApiSettings?.nonce;
  if (wpNonce) {
    headers["X-WP-Nonce"] = wpNonce;
  }
  const token = localStorage.getItem("wp_jwt_token");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const endpointUsed = "/wvc/v1/upload-video";
  const response = await fetch(endpointUsed, {
    method: "POST",
    headers,
    body: formData
  });
  if (!response.ok) {
    const errorBody = await response.text();
    let parsedError = errorBody;
    try {
      parsedError = JSON.stringify(JSON.parse(errorBody), null, 2);
    } catch {}
    throw new Error(`Failed to upload video to server. Endpoint used: ${endpointUsed}\n` + `HTTP Status: ${response.status} ${response.statusText}\n` + `Full Response Body:\n${parsedError}`);
  }
  console.log('Upload response:', response);
  const responseJson = await response.json().catch(() => ({}));
  console.log("Full video upload response JSON:\n" + JSON.stringify(responseJson, null, 2));
  let videoUrl: string | undefined = undefined;
  if (responseJson && typeof responseJson === "object") {
    videoUrl = responseJson?.media_details?.sizes?.full?.source_url || responseJson?.guid?.rendered || responseJson?.url;
  }
  if (!videoUrl && response.url) {
    videoUrl = response.url;
  }
  if (!videoUrl) {
    console.warn("Video URL not found in response object or response url:", responseJson);
  }
  if (!videoUrl) {
    console.error("Video URL not found in response object:", responseJson);
    throw new Error(`Video uploaded successfully to ${endpointUsed}, but no valid video URL was found in the response.\n` + `Response Body:\n${JSON.stringify(responseJson, null, 2)}`);
  }
  return videoUrl;
};
export default function Expertise() {
  const [uploadingIndices, setUploadingIndices] = useState<Record<number, boolean>>({});
  const [uploadErrors, setUploadErrors] = useState<Record<number, string>>({});
  const [videoURLs, setVideoURLs] = useState<Record<number, string | null>>({});
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  useEffect(() => {
    const cleanupFns: (() => void)[] = [];
    videoRefs.current.forEach(video => {
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      const playVideo = () => {
        if (!video) return;
        if (video.readyState === 0) {
          video.load();
        }
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      };
      if (video.readyState >= 2) {
        playVideo();
      }
      video.addEventListener("loadeddata", playVideo);
      video.addEventListener("canplay", playVideo);
      cleanupFns.push(() => {
        video.removeEventListener("loadeddata", playVideo);
        video.removeEventListener("canplay", playVideo);
      });
    });
    let observer: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const video = entry.target as HTMLVideoElement;
            if (video.readyState === 0) {
              video.load();
            }
            video.muted = true;
            const playPromise = video.play();
            if (playPromise !== undefined) {
              playPromise.catch(() => {});
            }
          }
        });
      }, {
        threshold: 0.25
      });
      videoRefs.current.forEach(video => {
        if (video && observer) {
          observer.observe(video);
        }
      });
      cleanupFns.push(() => {
        if (observer) {
          observer.disconnect();
        }
      });
    }
    const handleGestureUnlock = () => {
      videoRefs.current.forEach(video => {
        if (!video) return;
        if (video.readyState === 0) {
          video.load();
        }
        video.muted = true;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {});
        }
      });
      document.removeEventListener("touchstart", handleGestureUnlock);
      document.removeEventListener("pointerdown", handleGestureUnlock);
      document.removeEventListener("scroll", handleGestureUnlock);
    };
    document.addEventListener("touchstart", handleGestureUnlock, {
      once: true,
      passive: true
    });
    document.addEventListener("pointerdown", handleGestureUnlock, {
      once: true,
      passive: true
    });
    document.addEventListener("scroll", handleGestureUnlock, {
      once: true,
      passive: true
    });
    cleanupFns.push(() => {
      document.removeEventListener("touchstart", handleGestureUnlock);
      document.removeEventListener("pointerdown", handleGestureUnlock);
      document.removeEventListener("scroll", handleGestureUnlock);
    });
    return () => {
      cleanupFns.forEach(cleanup => cleanup());
    };
  }, [videoURLs]);
  const handleVideoChange = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadErrors(prev => ({
        ...prev,
        [index]: ""
      }));
      if (!file.type.startsWith("video/")) {
        setUploadErrors(prev => ({
          ...prev,
          [index]: "Invalid file type - please select a video"
        }));
        e.target.value = '';
        return;
      }
      if (file.size > 100 * 1024 * 1024) {
        setUploadErrors(prev => ({
          ...prev,
          [index]: "File too large (max 100MB)"
        }));
        e.target.value = '';
        return;
      }
      setUploadingIndices(prev => ({
        ...prev,
        [index]: true
      }));
      toast.loading('Uploading video...');
      try {
        const url = await uploadVideoToServer(file);
        if (!url) {
          toast.error('Upload failed - server returned no video URL');
          setUploadErrors(prev => ({
            ...prev,
            [index]: "Upload failed - please try again"
          }));
          return;
        }
        setVideoURLs(prev => ({
          ...prev,
          [index]: url
        }));
        toast.success("Video uploaded successfully");
      } catch (err) {
        console.error("Error uploading video", err);
        toast.error("Failed to upload video. Please try again.");
        setUploadErrors(prev => ({
          ...prev,
          [index]: "Upload failed - please try again"
        }));
      } finally {
        setUploadingIndices(prev => ({
          ...prev,
          [index]: false
        }));
        e.target.value = '';
      }
    }
  };
  const handleDeleteVideo = (index: number) => {
    setVideoURLs(prev => {
      const next = {
        ...prev
      };
      delete next[index];
      return next;
    });
  };
  const openMediaLibrary = (index: number) => {
    if (wpMedia) {
      const mediaUploader = wpMedia({
        title: 'Select Video',
        button: {
          text: 'Use this video'
        },
        multiple: false,
        library: {
          type: 'video'
        }
      });
      mediaUploader.on('select', () => {
        const attachment = mediaUploader.state().get('selection').first().toJSON();
        if (attachment && attachment.url) {
          setVideoURLs(prev => ({
            ...prev,
            [index]: attachment.url
          }));
          toast.success("Video selected successfully");
        }
      });
      mediaUploader.open();
    } else {
      toast.error("WordPress media library is not available.");
    }
  };
  useEffect(() => {
    return () => {
      Object.values(videoURLs).forEach(url => {
        if (url && url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [videoURLs]);
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const disciplines = [{
    number: "01",
    title: "AFTERMOVIE\n",
    description: "Voor TryBetter produceerden we een energieke aftermovie van Reurpop. Met dynamische beelden en een vlotte montage brachten we de sfeer, het publiek en de hoogtepunten van het evenement samen in één krachtige video.",
    videoPlaceholder: "Upload Strategy Reel",
    iClassName: "relative w-full h-full max-w-[280px] aspect-[9/16] border border-dashed border-[hsl(0,0%,30%)] group-hover:border-[hsl(30,100%,50%)] transition-colors duration-500 flex flex-col items-center justify-center gap-4 bg-[hsl(0,0%,15%)] !bg-[url(https://outgoing-horse.10web.cloud/wp-content/uploads/2026/07/aftermovie-trybetter-v2-1.mp4)]",
    defaultVideoSrc: "https://outgoing-horse.10web.cloud/wp-content/uploads/2026/07/aftermovie-trybetter-v2-1.mp4"
  }, {
    number: "02",
    title: "CONTENT IN ACTIE\n",
    description: "Voor Kuster maakten wij een vacaturevideo waarin de openstaande functie op een korte, persoonlijke en aantrekkelijke manier wordt gepresenteerd. Zo krijgt de potentiële kandidaat niet alleen de functie te zien, maar ook een beter beeld van het bedrijf en de werksfeer.\n",
    videoPlaceholder: "Upload Content Reel",
    iClassName: "absolute inset-0 z-20 flex flex-col items-center justify-center gap-4 cursor-pointer p-4 text-center",
    defaultVideoSrc: "https://socialcomplex.nl/wp-content/uploads/2026/08/kuster-energy-recruit-v3-logo-einde.mp4"
  }];
  return <section data-section-id="2840" id="onze-expertise" className="bg-[hsl(0,0%,10%)] text-white py-24 md:py-40 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 relative">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-[66.66%_33.33%] gap-12 lg:gap-24 mb-24 md:mb-32 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative">
            <div className="flex items-center gap-4 mb-8">
              
            </div>
            <h2 className="font-sans text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.0] tracking-tighter font-extrabold uppercase text-white">
              WERK WAAR WIJ<br />&nbsp;<span className="text-[hsl(30,100%,50%)]" data-ast-id="f:/src/pages/Home/sections/Expertise.tsx|sig:m1dndu" data-ast-start="2632" data-ast-end="2724">TROTS OP ZIJN</span>.
            </h2>
          </div>
          <div className="flex items-end lg:pb-4">
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 gap-px bg-[hsl(0,0%,20%)] transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] delay-200 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {disciplines.map((item, i) => <article data-index={i} key={item.number} className="group relative flex flex-col overflow-hidden bg-[hsl(0,0%,13%)] transition-all duration-500 cursor-pointer">
              
              
              
              
              <div className="relative w-full h-[500px] md:h-[700px] lg:h-[800px] overflow-hidden flex items-center justify-center bg-black">
                {videoURLs[i] ? <video ref={el => {
              videoRefs.current[i] = el;
            }} src={videoURLs[i]!} autoPlay muted playsInline loop controls preload="auto" className="w-full h-full object-cover cursor-pointer pointer-events-auto" /> : item.defaultVideoSrc ? <video ref={el => {
              videoRefs.current[i] = el;
            }} src={item.defaultVideoSrc} autoPlay muted playsInline loop controls preload="auto" className="w-full h-full object-cover cursor-pointer pointer-events-auto" /> : null}
              </div>

              <div className="absolute top-8 left-8 z-10">
              </div>

              <div className="absolute top-8 right-8 z-10 transition-transform duration-500 group-hover:translate-x-1">
                
              </div>

              <div className="p-6 bg-[hsl(0,0%,13%)] flex flex-col flex-grow justify-between">
                <div>
                  {i === 0 ? <motion.h3 initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                ease: "easeOut"
              }} className="font-sans text-2xl md:text-3xl leading-tight text-white font-bold uppercase mb-3 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-1">
                      {item.title}
                    </motion.h3> : <h3 className="font-sans text-2xl md:text-3xl leading-tight text-white font-bold uppercase mb-3 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:-translate-y-1">
                      {item.title}
                    </h3>}
                  <div className="h-px w-12 bg-[hsl(30,100%,50%)] mb-4" />
                  <p className="font-sans text-sm leading-[1.6] text-[hsl(0,0%,70%)]">
                    {i === 0 ? <>Voor TryBetter produceerden we een <strong className="text-white font-semibold">energieke aftermovie</strong> van <strong className="text-white font-semibold">Reurpop</strong>. Met dynamische beelden en een vlotte montage brachten we de sfeer, het publiek en de hoogtepunten van het evenement samen in één krachtige video.</> : i === 1 ? <>Voor Kuster maakten wij een <strong className="text-white font-semibold">vacaturevideo</strong> waarin de openstaande functie op <strong className="text-white font-semibold">een korte, persoonlijke en aantrekkelijke manier</strong> wordt gepresenteerd. Zo krijgt de <strong className="text-white font-semibold">potentiële kandidaat</strong> niet alleen de functie te zien, maar ook een beter beeld van het <strong className="text-white font-semibold">bedrijf en de werksfeer</strong>.</> : item.description}
                  </p>
                  {i === 0 && <div className="mt-6">
                      <Button asChild className="px-6 py-3 text-sm font-bold uppercase rounded-lg border-2 border-[hsl(30,100%,50%)] bg-[hsl(30,100%,50%)] hover:bg-[hsl(30,95%,48%)] text-[hsl(0,0%,10%)] transition-all duration-300">
                        <Link to="#contact">Vraag offerte aan</Link>
                      </Button>
                    </div>}
                </div>
              </div>

              <div className="absolute inset-0 border-2 border-transparent group-hover:border-[hsl(30,100%,50%)] transition-colors duration-500 pointer-events-none z-30" />
            </article>)}
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 15
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      ease: "easeOut"
    }} className="flex justify-start mt-24 md:mt-32 mx-auto md:mx-0 pb-12">
          <motion.div animate={{
        scale: [1, 1.05, 1]
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.97
      }}>
            <Button asChild className="px-8 py-4 text-base font-bold uppercase rounded-lg border-2 border-[hsl(30,100%,50%)] bg-[hsl(30,100%,50%)] hover:bg-[hsl(30,95%,48%)] text-[hsl(0,0%,10%)] transition-all duration-300 shadow-xl">
              
            </Button>
          </motion.div>
        </motion.div>
    </section>;
}